// client/src/components/pages/LexiStar/Dwell/dwellLogic.ts
//
// Dwell analytics logic (ST-00188 M4-D, T7-S04; LS-03 FR-STAT-007 / FR-RPT-001
// "average cycle and dwell times per site"; m4-contracts §3 dwell_limits, §4
// GET /dwell, GET/POST/PATCH /dwell-limits). The server computes the
// distributions, the per-context limits and the exceedance days (the
// ANALYTICS.DWELL_EXCEEDED events); this file only orders and labels them and
// validates a limit before it is sent.

import type {
  DwellContext,
  DwellDistribution,
  DwellExceedance,
  DwellLimit,
  DwellLimitInput,
  DwellResponse,
  DwellSite,
} from '../../../../types/lexistar-analytics';

export const CONTEXTS: { id: DwellContext; label: string; help: string }[] = [
  { id: 'AT_SOURCE', label: 'Loading', help: 'Time inside the source geofence (loading).' },
  { id: 'AT_DESTINATION', label: 'Offloading', help: 'Time inside the destination geofence (offloading / tipping).' },
  { id: 'IN_DELAY_ZONE', label: 'Waiting', help: 'Time in delay zones and queues (waiting).' },
  { id: 'AT_WEIGHBRIDGE', label: 'Weighbridge', help: 'Time on or at the weighbridge.' },
];

export const contextLabel = (c: DwellContext | string): string => CONTEXTS.find((x) => x.id === c)?.label ?? String(c);

export const isContext = (c: unknown): c is DwellContext => CONTEXTS.some((x) => x.id === c);

export interface SiteView {
  location_id: number;
  name: string;
  visits: number;
  over: number;
  contexts: DwellDistribution[];
  /** ST-00282: the site's dwell zones whose Alerts switch is off (their names). */
  alertsOffZones: string[];
}

/**
 * ST-00282: the one-line note under a site whose zones have alerts off — their visits count in
 * the figures, but only the visits to zones with alerts on can raise a dwell alert (the server
 * decides on the median of those). null when every zone alerts.
 */
export function alertsOffNote(zones: readonly string[]): string | null {
  if (!zones.length) return null;
  const names =
    zones.length === 1
      ? zones[0]
      : `${zones.slice(0, -1).join(', ')} and ${zones[zones.length - 1]}`;
  const their = zones.length === 1 ? 'its' : 'their';
  return `Alerts are off for ${names}: ${their} visits count in these figures, but only visits to zones with alerts on can raise a dwell alert.`;
}

/** Sites with visits, busiest first; each site's contexts in the fixed loading → weighbridge order. */
export function siteViews(body: DwellResponse | null | undefined): SiteView[] {
  return (body?.sites || [])
    .map((s: DwellSite) => {
      const contexts = CONTEXTS.map((c) => s.contexts.find((d) => d.context === c.id)).filter(
        (d): d is DwellDistribution => Boolean(d && d.visits > 0)
      );
      return {
        location_id: s.location_id,
        name: s.name || `Site ${s.location_id}`,
        visits: contexts.reduce((a, d) => a + d.visits, 0),
        over: contexts.reduce((a, d) => a + (d.over_limit_visits || 0), 0),
        contexts,
        alertsOffZones: s.alerts_off_zones || [],
      };
    })
    .filter((s) => s.contexts.length > 0)
    .sort((a, b) => b.over - a.over || b.visits - a.visits || a.name.localeCompare(b.name));
}

/** How far a day ran past its limit: the larger of the two medians (ST-00282: on_p50_min). */
export const overrunOf = (x: DwellExceedance): number => Math.max(x.p50_min, x.on_p50_min ?? -Infinity) - x.limit_min;

/** Exceedance days, latest first, then the worst overrun. */
export function sortedExceedances(rows: DwellExceedance[] | null | undefined): DwellExceedance[] {
  return [...(rows || [])].sort(
    (a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0) || overrunOf(b) - overrunOf(a)
  );
}

/** Width of a bin; the open-ended last bin takes the width of the one before it (or 15 min). */
export function binEnd(bins: DwellDistribution['histogram'], i: number): number {
  const b = bins[i];
  if (!b) return 0;
  if (b.to_min !== null && b.to_min !== undefined) return b.to_min;
  const prev = bins[i - 1];
  const width = prev && prev.to_min !== null ? prev.to_min - prev.from_min : 15;
  return b.from_min + Math.max(1, width);
}

/** Upper end of a distribution's axis: the last bin's end, p90 or the limit — whichever is largest. */
export function axisMax(d: DwellDistribution): number {
  const bins = d.histogram || [];
  const lastBin = bins.length ? binEnd(bins, bins.length - 1) : 0;
  return Math.max(1, lastBin, d.p90_min ?? 0, (d.limit_min ?? 0) * 1.1);
}

export interface LimitDraft {
  location_id: string;
  context: DwellContext | '';
  max_min: string;
  min_loading_min: string;
}

export const emptyDraft = (): LimitDraft => ({ location_id: '', context: '', max_min: '', min_loading_min: '' });

export const draftOf = (l: DwellLimit): LimitDraft => ({
  location_id: String(l.location_id),
  context: l.context,
  max_min: String(l.max_min),
  min_loading_min: l.min_loading_min === null || l.min_loading_min === undefined ? '' : String(l.min_loading_min),
});

/** Validate a draft → the POST / PATCH body, or the first problem in plain words. */
export function validateDraft(
  d: LimitDraft,
  existing: DwellLimit[],
  editingId: string | null = null
): { ok: true; body: DwellLimitInput } | { ok: false; error: string } {
  const location = Number(d.location_id);
  if (!d.location_id || !Number.isInteger(location)) return { ok: false, error: 'Choose a site.' };
  if (!isContext(d.context)) return { ok: false, error: 'Choose what the limit is for.' };
  const max = Number(d.max_min);
  if (!d.max_min || !Number.isFinite(max) || max <= 0 || !Number.isInteger(max)) {
    return { ok: false, error: 'The limit is a whole number of minutes above zero.' };
  }
  if (max > 24 * 60) return { ok: false, error: 'A dwell limit longer than 24 hours is not a limit — check the minutes.' };
  let minLoading: number | null = null;
  if (d.min_loading_min !== '') {
    if (d.context !== 'AT_SOURCE') return { ok: false, error: 'A minimum loading time applies to loading only.' };
    minLoading = Number(d.min_loading_min);
    if (!Number.isInteger(minLoading) || minLoading < 0) return { ok: false, error: 'The minimum loading time is whole minutes.' };
    if (minLoading >= max) return { ok: false, error: 'The minimum loading time must be shorter than the limit.' };
  }
  const clash = existing.find((l) => l.location_id === location && l.context === d.context && l.id !== editingId);
  if (clash) {
    return { ok: false, error: `${clash.location_name || 'This site'} already has a ${contextLabel(d.context).toLowerCase()} limit — edit that one.` };
  }
  return {
    ok: true,
    body: { location_id: location, context: d.context, max_min: max, min_loading_min: d.context === 'AT_SOURCE' ? minLoading : null },
  };
}

/** "1h 05m" / "48 min" for dwell minutes (null → —). */
export function fmtMin(m: number | null | undefined): string {
  if (m === null || m === undefined || !Number.isFinite(m)) return '—';
  const r = Math.round(m);
  if (r < 60) return `${r} min`;
  return `${Math.floor(r / 60)}h ${String(r % 60).padStart(2, '0')}m`;
}
